import { Button, Card, Empty, Space, Tag, Tooltip, Typography, theme as antdTheme } from 'antd';
import {
  ArrowRightOutlined,
  EditOutlined,
  PhoneOutlined,
  StopOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import {
  type Inquiry,
  type InquirySource,
  type InquiryStatus,
} from '@/api/frontdesk';
import { useAuthStore } from '@/store/auth';

/**
 * Funnel (kanban) rendering of inquiries — five columns reading the same
 * filtered data the table renders. No drag-and-drop dependency: each card
 * shows a one-tap "→ next stage" button that walks the inquiry through the
 * follow-up status flow.
 *
 * The stage order matches the backend enum order:
 *   NEW → CONTACTED → VISIT_SCHEDULED → ENROLLED → DROPPED
 *
 * ENROLLED + DROPPED are terminal — their cards show "Done" / no-op instead
 * of an advance button. Converting to a real Student record happens through
 * the same Register button the table uses (opens /students/new with the
 * inquiry id pre-filled).
 */

const SOURCE_LABEL: Record<InquirySource, string> = {
  WALK_IN: 'Walk-in',
  REFERRAL: 'Referral',
  PHONE: 'Phone',
  OTHER: 'Other',
};

const COLUMN_ORDER: InquiryStatus[] = ['NEW', 'CONTACTED', 'VISIT_SCHEDULED', 'ENROLLED', 'DROPPED'];
const COLUMN_LABEL: Record<InquiryStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  VISIT_SCHEDULED: 'Visit scheduled',
  ENROLLED: 'Enrolled',
  DROPPED: 'Dropped',
};

/** Tone-mapped to AntD presets so column headers restyle with the active theme. */
const COLUMN_TONE: Record<InquiryStatus, string> = {
  NEW: 'processing',
  CONTACTED: 'cyan',
  VISIT_SCHEDULED: 'gold',
  ENROLLED: 'success',
  DROPPED: 'default',
};

/** Next stage in the happy-path flow. ENROLLED + DROPPED are terminal. */
function nextStage(s: InquiryStatus): InquiryStatus | null {
  switch (s) {
    case 'NEW':              return 'CONTACTED';
    case 'CONTACTED':        return 'VISIT_SCHEDULED';
    case 'VISIT_SCHEDULED':  return 'ENROLLED';
    default:                 return null;
  }
}

interface Props {
  rows: readonly Inquiry[];
  loading?: boolean;
  onEdit: (row: Inquiry) => void;
  onAdvance: (id: number, next: InquiryStatus) => void;
  onDrop: (id: number) => void;
}

export function InquiryFunnel({ rows, loading, onEdit, onAdvance, onDrop }: Props) {
  const { token } = antdTheme.useToken();
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));
  const canRegister = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:CREATE'));
  const navigate = useNavigate();

  // Bucket the inquiries by stage. Convert-to-student short-circuits straight
  // to the ENROLLED column even if the row's followUpStatus hasn't been
  // touched, because the conversion is the "true" enrollment signal.
  const buckets: Record<InquiryStatus, Inquiry[]> = {
    NEW: [], CONTACTED: [], VISIT_SCHEDULED: [], ENROLLED: [], DROPPED: [],
  };
  for (const r of rows) {
    const key: InquiryStatus = r.convertedStudentId ? 'ENROLLED' : r.followUpStatus;
    buckets[key].push(r);
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(220px, 1fr))',
        gap: 12,
        overflowX: 'auto',
        opacity: loading ? 0.6 : 1,
        transition: 'opacity 180ms ease',
      }}
    >
      {COLUMN_ORDER.map((stage) => (
        <div
          key={stage}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            background: token.colorFillQuaternary,
            borderRadius: 10,
            padding: 10,
            minHeight: 200,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '2px 4px 8px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Typography.Text strong style={{ fontSize: 13 }}>
              {COLUMN_LABEL[stage]}
            </Typography.Text>
            <Tag color={COLUMN_TONE[stage]} style={{ marginInlineEnd: 0 }}>
              {buckets[stage].length}
            </Tag>
          </div>

          {buckets[stage].length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Empty</Typography.Text>}
              style={{ marginTop: 16 }}
            />
          ) : (
            buckets[stage].map((row) => (
              <FunnelCard
                key={row.id}
                row={row}
                canEdit={canEdit}
                canRegister={canRegister}
                onEdit={() => onEdit(row)}
                onAdvance={() => {
                  const next = nextStage(row.followUpStatus);
                  if (next) onAdvance(row.id, next);
                }}
                onDrop={() => onDrop(row.id)}
                onRegister={() => navigate(`/students/new?fromInquiry=${row.id}`)}
              />
            ))
          )}
        </div>
      ))}
    </div>
  );
}

interface CardProps {
  row: Inquiry;
  canEdit: boolean;
  canRegister: boolean;
  onEdit: () => void;
  onAdvance: () => void;
  onDrop: () => void;
  onRegister: () => void;
}

function FunnelCard({ row, canEdit, canRegister, onEdit, onAdvance, onDrop, onRegister }: CardProps) {
  const { token } = antdTheme.useToken();
  const next = nextStage(row.followUpStatus);
  const terminal = row.convertedStudentId != null || row.followUpStatus === 'DROPPED';

  return (
    <Card
      size="small"
      styles={{ body: { padding: 10 } }}
      style={{ background: token.colorBgContainer }}
    >
      <Space orientation="vertical" size={4} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'flex-start' }}>
          <Typography.Text strong ellipsis style={{ minWidth: 0 }}>
            {row.studentName}
          </Typography.Text>
          <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
            {SOURCE_LABEL[row.source]}
          </Tag>
        </div>

        {row.classInterestedName && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            for {row.classInterestedName}
          </Typography.Text>
        )}

        {row.guardianName && (
          <Typography.Text style={{ fontSize: 12 }} ellipsis>
            {row.guardianName}
          </Typography.Text>
        )}

        {row.guardianPhone && (
          /* `tel:` link triggers the OS dialer on mobile; harmless on desktop. */
          <a href={`tel:${row.guardianPhone}`} style={{ fontSize: 12 }}>
            <PhoneOutlined style={{ marginInlineEnd: 4 }} />
            {row.guardianPhone}
          </a>
        )}

        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {dayjs(row.inquiryDate).format('D MMM YYYY')}
          {row.assignedToStaffName && ` · ${row.assignedToStaffName}`}
        </Typography.Text>

        {row.convertedStudentRegistrationNumber && (
          <Tag color="success" style={{ fontSize: 11 }}>
            {row.convertedStudentRegistrationNumber}
          </Tag>
        )}

        {canEdit && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
            <Tooltip title="Edit inquiry">
              <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
            </Tooltip>
            {!terminal && canRegister && row.followUpStatus === 'VISIT_SCHEDULED' && (
              <Tooltip title="Register as student (pre-fills the form)">
                <Button size="small" type="text" icon={<UserAddOutlined />} onClick={onRegister}>
                  Register
                </Button>
              </Tooltip>
            )}
            {next && !terminal && (
              <Button
                size="small"
                type="primary"
                ghost
                icon={<ArrowRightOutlined />}
                onClick={onAdvance}
                style={{ marginInlineStart: 'auto' }}
              >
                {COLUMN_LABEL[next]}
              </Button>
            )}
            {!terminal && (
              <Tooltip title="Drop — won't pursue">
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<StopOutlined />}
                  onClick={onDrop}
                />
              </Tooltip>
            )}
          </div>
        )}
      </Space>
    </Card>
  );
}
